/**
 * Accounting checks, on screen (docs/tally/19; the screens are docs/tally/20
 * §4 and §8).
 *
 * The counts first, by how much they matter; then each kind of check, folded
 * to one line until opened, and opened it answers the five questions in the
 * same order every time — what was found, what it means, how Timely handles
 * it, whether anyone needs to act, and whether syncing can continue. The
 * accounts behind a check are listed only to people who may see them.
 *
 * Words describe the books, never the person keeping them. "Health" is not
 * used: these are facts about the figures, not a grade (PRODUCT-UX-FOUNDATION
 * §14).
 */
import { Badge, Disclosure, type Tone } from '@timely/ui';
import {
    SEVERITY_ORDER, SEVERITY_WORDS, checkDefinition, describeFinding,
    type CheckCode, type CheckSeverity, type Finding, type FindingsSummary,
} from '@timely/domain';
import { useFormatters } from '../book/useFormatters';

export const SEVERITY_TONE: Record<CheckSeverity, Tone> = { blocks: 'dang', attention: 'warn', review: 'neutral', information: 'neutral' };

/** The four counts, in one line of figures (never a row of KPI cards). */
export function CheckCounts({ summary }: { summary: FindingsSummary }) {
    const { count } = useFormatters();
    return (
        <dl className="flex flex-wrap gap-x-6 gap-y-2 text-body" aria-label="Accounting checks by level">
            {SEVERITY_ORDER.map(s => (
                <div key={s} className="flex items-baseline gap-2">
                    <dt className="text-label-3">{SEVERITY_WORDS[s]}</dt>
                    <dd className="num font-semibold text-label">{count(summary.counts[s])}</dd>
                </div>
            ))}
        </dl>
    );
}

export interface ChecksListProps {
    summary: FindingsSummary;
    /** The findings this person may see in full (each with its account, where it has one). */
    findings: readonly Finding[];
    /** Findings about accounts this person may not see are counted, not listed. */
    accountsHidden?: boolean;
    /** Beside each listed finding: its state and what may be done with it (acknowledge, for instance). */
    itemExtra?: (finding: Finding) => React.ReactNode;
}

export function ChecksList({ summary, findings, accountsHidden = false, itemExtra }: ChecksListProps) {
    const { money, date } = useFormatters();
    if (summary.byCheck.length === 0) {
        return <p className="text-body text-label-2">Nothing to report: every amount ties to an open bill or to a reason TallyPrime gives.</p>;
    }
    const byCode = new Map<CheckCode, Finding[]>();
    for (const f of findings) byCode.set(f.code, [...(byCode.get(f.code) ?? []), f]);
    return (
        <ul className="grid" aria-label="Accounting checks">
            {summary.byCheck.map(c => {
                const def = checkDefinition(c.code);
                const listed = byCode.get(c.code) ?? [];
                const words = (f: Finding) => describeFinding(f, n => money.exact(n), d => date.day(d));
                const sample = listed[0] ? words(listed[0]) : null;
                const amount = c.amount ? ` · ${money.exact(c.amount)}` : '';
                return (
                    <li key={c.code} className="-mt-px first:mt-0">
                        <Disclosure
                            title={def.title}
                            icon={<Badge variant="text" dot tone={SEVERITY_TONE[c.severity]} className="sm:w-44">{SEVERITY_WORDS[c.severity]}</Badge>}
                            summary={`${c.findings} ${c.findings === 1 ? 'item' : 'items'}${amount}`}
                        >
                            <dl className="grid gap-3 text-body">
                                <div>
                                    <dt className="text-caption font-semibold text-label-3">What Timely found</dt>
                                    <dd className="mt-0.5">
                                        {listed.length > 0 ? (
                                            <ul className="grid gap-1 text-label-2">
                                                {listed.slice(0, 50).map(f => <li key={f.fingerprint}>{words(f).found}{itemExtra && <div className="mt-0.5">{itemExtra(f)}</div>}</li>)}
                                                {listed.length > 50 && <li className="text-label-3">…and {listed.length - 50} more.</li>}
                                            </ul>
                                        ) : (
                                            <span className="text-label-2">
                                                {c.findings} {c.findings === 1 ? 'item' : 'items'}{amount}.{accountsHidden ? ' The accounts are listed to those who manage receivables data.' : ''}
                                            </span>
                                        )}
                                    </dd>
                                </div>
                                <div><dt className="text-caption font-semibold text-label-3">What it means</dt><dd className="mt-0.5 text-label-2">{sample?.means ?? def.means}</dd></div>
                                <div><dt className="text-caption font-semibold text-label-3">How Timely handles it</dt><dd className="mt-0.5 text-label-2">{def.handling}</dd></div>
                                <div><dt className="text-caption font-semibold text-label-3">Does anyone need to do anything</dt><dd className="mt-0.5 text-label-2">{def.action}</dd></div>
                                <div><dt className="text-caption font-semibold text-label-3">Can syncing continue</dt><dd className="mt-0.5 text-label-2">{def.sync}</dd></div>
                            </dl>
                        </Disclosure>
                    </li>
                );
            })}
        </ul>
    );
}

/** Findings from a stored preview: the company's own, and each account's (for those who may see accounts). */
export function previewFindings(input: {
    companyFindings: { code: CheckCode; severity: CheckSeverity; facts: { amount?: number; count?: number } }[];
    accounts?: { sourceKey: string; name: string; checks?: { code: CheckCode; severity: CheckSeverity; facts: Finding['facts'] }[] }[];
}): Finding[] {
    const company = input.companyFindings.map((f, i): Finding => ({ fingerprint: `${f.code}:company:${i}`, code: f.code, severity: f.severity, facts: f.facts }));
    const accounts = (input.accounts ?? []).flatMap(a => (a.checks ?? []).map((f, i): Finding => ({
        fingerprint: `${f.code}:${a.sourceKey}:${i}`, code: f.code, severity: f.severity, sourceKey: a.sourceKey, account: a.name, facts: f.facts,
    })));
    return [...company, ...accounts].sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity));
}
